import { AxiosError } from "axios"

import type { ResponseConfig } from "~/backend/axiosClient"
import {
  findFeatureFlagByName,
  useFindFeatureFlagByName as useGeneratedFindFeatureFlagByName,
} from "~/gen/api/hooks/useFindFeatureFlagByName"
import type { FindFeatureFlagByNameQueryResponse } from "~/gen/api/types/FindFeatureFlagByName"
import * as Sentry from "@sentry/react"
import { FindFeatureFlagsByNameResponseDto } from "~/gen/api/types/FindFeatureFlagsByNameResponseDto"
const shouldRetryFeatureFlagQuery = (
  failureCount: number,
  error: unknown
): boolean => {
  if (failureCount > 3) return false

  if (error instanceof AxiosError) {
    return ![401, 403, 404].includes(error.response?.status ?? 0)
  }

  return true
}

const isNotFound = (e: unknown): boolean =>
  e instanceof AxiosError && e.response?.status === 404

type FeatureFlagQueryData = ResponseConfig<FindFeatureFlagByNameQueryResponse>

type FeatureFlagQueryResult = ReturnType<
  typeof useGeneratedFindFeatureFlagByName<
    FeatureFlagQueryData,
    FeatureFlagQueryData
  >
>

type FeatureFlagHookOptions = Parameters<
  typeof useGeneratedFindFeatureFlagByName<
    FeatureFlagQueryData,
    FeatureFlagQueryData
  >
>[1]

export const useFindFeatureFlagByName = (
  name: string | undefined,
  options?: FeatureFlagHookOptions
): FeatureFlagQueryResult => {
  const { query: queryConfig = {}, ...restOptions } = options ?? {}

  return useGeneratedFindFeatureFlagByName<
    FeatureFlagQueryData,
    FeatureFlagQueryData
  >(name, {
    ...restOptions,
    query: {
      ...queryConfig,
      retry: shouldRetryFeatureFlagQuery, // still useful for 401/403
      queryFn: async () => {
        try {
          return await findFeatureFlagByName(name!)
        } catch (error) {
          if (isNotFound(error)) {
            Sentry.addBreadcrumb({
              category: "feature-flag",
              message: `flag_absent_treated_as_disabled: ${name}`,
              level: "info",
            })
            return {
              data: {
                name: name!,
                description: "",
                category: "",
                state: false,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                ttlDays: new Date().toISOString(),
                defaultState: false,
              } as FindFeatureFlagsByNameResponseDto,
              status: 200,
              statusText: "OK",
              headers: {},
            } as FeatureFlagQueryData
          }
          throw error
        }
      },
    },
  })
}
